import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Sidebar } from './Sidebar';
import { Button } from '../ui/button';
import { Toaster } from '../ui/sonner';

// Verificar si el token es válido (duplicado aquí para verificación independiente)
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const payload = JSON.parse(atob(parts[1]));
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

export function DashboardLayout({ children }) {
  const { isAuthenticated, loading, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  
  // Verificar token al montar y periódicamente
  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem('avipork_token');
      if (token && !isTokenValid(token)) {
        console.log('[Layout] Token expirado detectado, forzando logout');
        logout();
      }
      setTokenChecked(true);
    };
    
    checkToken();
    
    // Verificar cada 30 segundos
    const interval = setInterval(checkToken, 30000);
    return () => clearInterval(interval);
  }, [logout]);

  // Mostrar loading solo por máximo 3 segundos
  const [forceShow, setForceShow] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (loading) {
        console.log('[Layout] Timeout de loading, forzando mostrar contenido');
        setForceShow(true);
      }
    }, 3000);
    return () => clearTimeout(timeout);
  }, [loading]);

  if (loading && !forceShow) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-[#931A25] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Verificar autenticación: estado de React O token válido en storage
  const token = localStorage.getItem('avipork_token');
  const hasValidToken = isTokenValid(token);
  
  if (!isAuthenticated && !hasValidToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 z-30 flex items-center px-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="open-sidebar-btn"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <h1 className="font-brand text-xl font-bold text-[#931A25] ml-4">AVIPORK</h1>
      </div>

      {/* Main content */}
      <main className="md:pl-64 pt-16 md:pt-0 min-h-screen">
        <div className="max-w-7xl mx-auto p-4 md:p-8">
          {children}
        </div>
      </main>

      <Toaster position="top-right" richColors />
    </div>
  );
}
